import { Outlet, useOutletContext, useParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { api, keys, useMe } from '../../api.ts';
import type { SessionUser, UserDetail } from '../../../shared/types.ts';
import { ErrorBox, Loading, PageHeader, Tabs, useDocumentTitle } from '../../components/ui.tsx';

export interface UserContext {
  user: UserDetail;
  me: SessionUser | null;
  /** ylläpitäjä tai käyttäjä itse */
  canEdit: boolean;
  isSelf: boolean;
}

export function useUserContext() {
  return useOutletContext<UserContext>();
}

export function UserLayout() {
  const { id = '' } = useParams();
  const { data: me } = useMe();
  const q = useQuery({
    queryKey: [...keys.user(id), !!me],
    queryFn: () => api.get<UserDetail>(`/users/${encodeURIComponent(id)}`),
  });
  useDocumentTitle(q.data?.id);

  if (q.isLoading) return <Loading />;
  if (q.error || !q.data) return <ErrorBox error={q.error ?? new Error('Käyttäjää ei löytynyt')} />;

  const user = q.data;
  const isSelf = !!me && me.id.toUpperCase() === user.id.toUpperCase();
  const canEdit = !!me && (me.isAdmin || isSelf);
  const base = `/users/${encodeURIComponent(user.id)}`;

  return (
    <div className="page">
      <PageHeader
        title={
          <>
            <span className="call">{user.id}</span> <span className="muted" style={{ fontWeight: 400 }}>{user.name}</span>
          </>
        }
      />
      <Tabs
        items={[
          { to: base, label: isSelf ? 'Omat tiedot' : 'Tiedot' },
          !!me && { to: `${base}/qsos`, label: isSelf ? 'Omat QSO:t' : 'QSO:t' },
          !!me && { to: `${base}/matrix`, label: 'DXCC-taulukko' },
          canEdit && { to: `${base}/edit`, label: 'Muokkaa tietoja' },
          canEdit && { to: `${base}/fixed/ohcw`, label: 'OH-CW & Divisions' },
          canEdit && { to: `${base}/fixed/band-mode`, label: 'Band-Mode-arvot' },
          canEdit && { to: `${base}/fixed/marathon`, label: 'Marathon-arvot' },
        ]}
      />
      <Outlet context={{ user, me: me ?? null, canEdit, isSelf } satisfies UserContext} />
    </div>
  );
}
